/**
 * Ask the AI panel a question from anywhere in the app (18.08.2026).
 *
 * Until now the assistant had exactly one way in: open the panel and type.
 * Every moment where help is obvious — a request came back 401, a test failed,
 * SQL errored — offered nothing, and the user had to re-describe on the panel
 * side what was already on screen on the other.
 *
 * This is the missing primitive. It is an EVENT rather than props because the
 * callers (RequestBuilder, ResponseViewer, DbResultsGrid) sit in completely
 * different subtrees from AIChatPanel; wiring a callback down would mean
 * threading it through App and every layer between. The codebase already uses
 * this shape for cross-subtree signals (VAULT_GATE_UNLOCKED_EVENT and friends).
 *
 * `send: false` (the default) FILLS the composer and lets the user edit or
 * discard before sending. That default is deliberate: an inline button that
 * silently spends the user's own API-key money the instant it is clicked is a
 * button people learn not to press.
 */
import { setAiPanelOpen } from '../../store/uiModalStore';

export const AI_PREFILL_EVENT = 'ai-prefill';

/**
 * Open the AI panel with the composer prefilled.
 *
 * @param {string}   text        - prompt to place in the composer
 * @param {Array}    references  - @-mention chips to attach (buildReferenceOptions shape)
 * @param {boolean}  send        - submit immediately instead of waiting for the user
 */
export function askAi({ text, references = [], send = false } = {}) {
  if (!text || !String(text).trim()) return;

  setAiPanelOpen(true);

  // The panel may be mounting for the first time — its listener does not exist
  // until after this tick. One frame is enough for React to commit the mount,
  // and a missed prefill is a silently empty composer.
  requestAnimationFrame(() => {
    window.dispatchEvent(
      new CustomEvent(AI_PREFILL_EVENT, { detail: { text: String(text), references, send } })
    );
  });
}
